import React, { useState, useEffect, useCallback, useRef } from 'react';
import notify from './utils/notify';
import { useNavigate } from 'react-router-dom';
import {
  FiDownload, FiPlay, FiPause, FiX, FiRefreshCw,
  FiTrash2, FiPlus, FiCheck, FiAlertCircle, FiLoader,
  FiRotateCcw, FiChevronDown, FiChevronUp, FiChevronRight, FiCpu,
} from 'react-icons/fi';
import { FiBarChart2, FiGrid, FiSettings } from 'react-icons/fi';
import { GiGolfTee } from 'react-icons/gi';
import { useAuth } from './AuthContext';
import BottomNav from './BottomNav';
import PageHeader from './components/PageHeader';
import ConfirmationModal from './components/ConfirmationModal';
import { COUNTRIES, regionOptions, isKnownRegion } from './utils/countryRegions';
import './CourseDownloader.css';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const STATUS_META = {
  generating: { label: 'Generating',  color: '#8b5cf6' },
  ready:      { label: 'Ready',       color: '#3b82f6' },
  running:    { label: 'Running',     color: '#10b981' },
  paused:     { label: 'Paused',      color: '#f59e0b' },
  complete:   { label: 'Complete',    color: '#6b7280' },
  failed:     { label: 'Failed',      color: '#ef4444' },
  cancelled:  { label: 'Cancelled',   color: '#9ca3af' },
};


const TIERS = [
  { value: 'top',     label: 'Top / Major',        hint: 'Championship venues, Top-100 lists' },
  { value: 'popular', label: 'Well-known',          hint: 'Highly-rated, widely played' },
  { value: 'all',     label: 'All accessible',     hint: 'Broad search including club courses' },
  { value: 'public',  label: 'Public / Pay & Play', hint: 'Municipal and public-access courses' },
];

const TYPES = [
  { value: 'links',        label: 'Links' },
  { value: 'parkland',     label: 'Parkland' },
  { value: 'heathland',    label: 'Heathland' },
  { value: 'resort',       label: 'Resort' },
  { value: 'championship', label: 'Championship' },
];

const DEFAULT_FORM = {
  country: 'United Kingdom',
  region: '',
  tier: 'top',
  types: [],
  targetCount: 100,
  delaySeconds: 5,
};

// The area is NOT part of the description: it is sent as `region`, which the
// server treats as a boundary on every search rather than a phrase in one.
function buildDescription(tier, types) {
  const phrases = { top: 'top-ranked and major championship', popular: 'popular and well-known', all: 'established', public: 'public and pay-and-play' };
  const typePhrase = types.length > 0 ? ` ${types.join(' and ')}` : '';
  return `${phrases[tier] || 'golf'}${typePhrase} golf courses`;
}

// ---------------------------------------------------------------------------
// Pipeline helpers
// ---------------------------------------------------------------------------

function getStepStatuses(batch) {
  const s = batch.status;
  const hasTotal = (batch.total_count || 0) > 0;
  const hasProgress = (batch.processed_count || 0) > 0;

  if (s === 'generating') return ['active',    'pending',   'pending',   'pending'];
  if (s === 'ready')      return ['done',      'active',    'pending',   'pending'];
  if (s === 'running')    return ['done',      'done',      'active',    'pending'];
  if (s === 'paused')     return ['done',      'done',      'paused',    'pending'];
  if (s === 'complete')   return ['done',      'done',      'done',      'done'];
  if (s === 'failed') {
    if (!hasTotal)         return ['failed',   'pending',   'pending',   'pending'];
    return                        ['done',     'done',      'failed',    'pending'];
  }
  if (s === 'cancelled') {
    if (!hasTotal)         return ['cancelled','pending',   'pending',   'pending'];
    if (!hasProgress)      return ['done',     'cancelled', 'pending',   'pending'];
    return                        ['done',     'done',      'cancelled', 'pending'];
  }
  return ['pending', 'pending', 'pending', 'pending'];
}

const STEP_STATUS_LABEL = {
  done: 'Done', active: 'In progress', paused: 'Paused',
  failed: 'Failed', cancelled: 'Cancelled', pending: '',
};

function confColor(score) {
  if (score >= 0.8) return '#10b981';
  if (score >= 0.5) return '#f59e0b';
  return '#ef4444';
}

// ---------------------------------------------------------------------------
// Shared small components
// ---------------------------------------------------------------------------

function StatusBadge({ status }) {
  const meta = STATUS_META[status] || { label: status, color: '#9ca3af' };
  return <span className="cd-badge" style={{ background: meta.color }}>{meta.label}</span>;
}

function StepNode({ ss }) {
  return (
    <div className={`cd-pipe-node cd-pipe-node--${ss}`}>
      {ss === 'done'      && <FiCheck size={11} />}
      {ss === 'active'    && <FiLoader className="cd-spin" size={11} />}
      {ss === 'paused'    && <FiPause size={10} />}
      {ss === 'failed'    && <FiAlertCircle size={11} />}
      {ss === 'cancelled' && <FiX size={10} />}
    </div>
  );
}

function ImportJobRow({ job }) {
  const [open, setOpen] = useState(false);
  const telemetry = Array.isArray(job.telemetry) ? job.telemetry : [];
  const hasTelemetry = telemetry.length > 0;

  return (
    <div className={`cd-ingest-row${job.status === 'running' ? ' cd-ingest-row--active' : ''}`}>
      <div className="cd-ingest-main" onClick={() => hasTelemetry && setOpen(o => !o)} style={hasTelemetry ? { cursor: 'pointer' } : {}}>
        <span className="cd-ingest-icon">
          {job.status === 'pending' && <span className="cd-ingest-dot" />}
          {job.status === 'running' && <FiLoader className="cd-spin" size={11} style={{ color: '#10b981' }} />}
          {job.status === 'success' && <FiCheck size={11} style={{ color: '#10b981' }} />}
          {job.status === 'failed'  && <FiAlertCircle size={11} style={{ color: '#ef4444' }} />}
        </span>
        <span className={`cd-ingest-name${job.status === 'running' ? ' cd-ingest-name--active' : ''}`}>
          {job.course_name}
        </span>
        {job.status === 'success' && job.confidence_score != null && (
          <span className="cd-conf-pill" style={{ background: confColor(job.confidence_score) }}>
            {Math.round(job.confidence_score * 100)}%
          </span>
        )}
        {job.status === 'failed' && job.failed_stage && (
          <span className="cd-stage-pill">@ {job.failed_stage}</span>
        )}
        {job.status === 'failed' && job.retry_count > 0 && (
          <span className="cd-retry-pill">retry #{job.retry_count}</span>
        )}
        {job.status === 'failed' && job.error_message && !job.failed_stage && (
          <span className="cd-ingest-error" title={job.error_message}>
            {job.error_message.length > 40 ? job.error_message.slice(0, 38) + '…' : job.error_message}
          </span>
        )}
        {/* What this course cost. The token counts are facts the job recorded;
            the dollar figure is an estimate and is simply absent for a model
            with no published price in the table, rather than shown as 0. */}
        {job.estimated_cost_usd != null && (
          <span className="cd-cost-pill" title={`${(job.tokens_in || 0).toLocaleString()} in / ${(job.tokens_out || 0).toLocaleString()} out`}>
            ${job.estimated_cost_usd.toFixed(3)}
          </span>
        )}
      </div>

      {open && hasTelemetry && (
        <div className="cd-telemetry-stages">
          {telemetry.map((ev, i) => (
            <div key={i} className={`cd-tel-row cd-tel-row--${ev.status || 'info'}`}>
              <span className="cd-tel-stage">{ev.stage}</span>
              <span className="cd-tel-msg">{ev.message}</span>
              {/* Which model ANSWERED this stage — not the one configured for
                  it. They differ whenever a fallback did the work, which is
                  the thing you want to see when quality drops. */}
              {ev.model && <span className="cd-tel-model">{ev.model.replace(/^gemini-/, '')}</span>}
              {ev.duration_ms != null && <span className="cd-tel-dur">{(ev.duration_ms / 1000).toFixed(1)}s</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Quality summary bar
// ---------------------------------------------------------------------------

function QualitySummary({ summary }) {
  if (!summary) return <div className="cd-pipe-subtext"><FiLoader className="cd-spin" size={10} /> Loading summary…</div>;

  const { success, failed, high_confidence, medium_confidence, low_confidence, avg_confidence, stage_failures } = summary;
  const total = success + failed;

  return (
    <div className="cd-quality-summary">
      <div className="cd-quality-row">
        <div className="cd-quality-stat cd-quality-stat--ok">
          <span className="cd-quality-num">{success}</span>
          <span className="cd-quality-lbl">imported</span>
        </div>
        <div className="cd-quality-stat cd-quality-stat--fail">
          <span className="cd-quality-num">{failed}</span>
          <span className="cd-quality-lbl">failed</span>
        </div>
        {avg_confidence != null && (
          <div className="cd-quality-stat">
            <span className="cd-quality-num" style={{ color: confColor(avg_confidence) }}>
              {Math.round(avg_confidence * 100)}%
            </span>
            <span className="cd-quality-lbl">avg confidence</span>
          </div>
        )}
      </div>

      {success > 0 && (
        <div className="cd-conf-bar-wrap">
          <div className="cd-conf-bar">
            <div className="cd-conf-seg cd-conf-seg--high"  style={{ width: `${(high_confidence  / total) * 100}%` }} title={`High: ${high_confidence}`} />
            <div className="cd-conf-seg cd-conf-seg--med"   style={{ width: `${(medium_confidence / total) * 100}%` }} title={`Medium: ${medium_confidence}`} />
            <div className="cd-conf-seg cd-conf-seg--low"   style={{ width: `${(low_confidence   / total) * 100}%` }} title={`Low: ${low_confidence}`} />
            <div className="cd-conf-seg cd-conf-seg--fail"  style={{ width: `${(failed           / total) * 100}%` }} title={`Failed: ${failed}`} />
          </div>
          <div className="cd-conf-legend">
            {high_confidence  > 0 && <span className="cd-conf-l cd-conf-l--high">{high_confidence} high</span>}
            {medium_confidence > 0 && <span className="cd-conf-l cd-conf-l--med">{medium_confidence} med</span>}
            {low_confidence   > 0 && <span className="cd-conf-l cd-conf-l--low">{low_confidence} low</span>}
          </div>
        </div>
      )}

      {Object.keys(stage_failures || {}).length > 0 && (
        <div className="cd-stage-fail-list">
          <span className="cd-stage-fail-label">Failures by stage:</span>
          {Object.entries(stage_failures).map(([stage, count]) => (
            <span key={stage} className="cd-stage-fail-item">{stage}: {count}</span>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Vertical pipeline component
// ---------------------------------------------------------------------------

const PIPELINE_STEPS = [
  { id: 'generate', label: 'Generate Course List',   hint: 'AI builds a targeted list via grounded web search' },
  { id: 'queue',    label: 'Deduplicate & Queue',    hint: 'Remove courses already in the directory, stage the rest' },
  { id: 'import',   label: 'Import & Extract',       hint: 'Fetch WHS slope, course rating and hole data per course' },
  { id: 'summary',  label: 'Quality Summary',        hint: 'Confidence scoring and data quality review' },
];

function stepCollapsedSummary(stepId, ss, batch, detailData, genLog, pct, qualitySummary) {
  if (stepId === 'generate') {
    if (ss === 'active') return 'Searching…';
    if (genLog.length > 0) {
      const last = genLog[genLog.length - 1];
      return last?.msg ? (last.msg.length > 55 ? last.msg.slice(0, 53) + '…' : last.msg) : null;
    }
  }
  if (stepId === 'queue') {
    if (batch.total_count > 0)
      return `${batch.total_count} queued${batch.skipped_count > 0 ? ` · ${batch.skipped_count} skipped` : ''}`;
    if (ss === 'active' && detailData?.type === 'courses')
      return `${detailData.list.length} courses staged`;
  }
  if (stepId === 'import' && batch.total_count > 0)
    return `${batch.processed_count}/${batch.total_count} · ${pct}%${batch.failed_count > 0 ? ` · ${batch.failed_count} failed` : ''}`;
  if (stepId === 'summary' && qualitySummary?.avg_confidence != null)
    return `Avg ${Math.round(qualitySummary.avg_confidence * 100)}% · ${qualitySummary.high_confidence} high / ${qualitySummary.medium_confidence} med / ${qualitySummary.low_confidence} low`;
  return null;
}

function WorkflowPipeline({ batch, detailData, detailLoading, qualitySummary, expandedSteps, toggleStep }) {
  const stepStatuses = getStepStatuses(batch);
  const genLog = Array.isArray(batch.generation_log) ? batch.generation_log : [];
  const pct = batch.total_count > 0 ? Math.round((batch.processed_count / batch.total_count) * 100) : 0;

  return (
    <div className="cd-pipeline">
      {PIPELINE_STEPS.map((step, i) => {
        const ss = stepStatuses[i];
        const isLast = i === PIPELINE_STEPS.length - 1;
        const statusLabel = STEP_STATUS_LABEL[ss];
        const hasContent = ss !== 'pending' && ss !== 'cancelled' && (
          (step.id === 'generate') ||
          (step.id === 'queue') ||
          (step.id === 'import') ||
          (step.id === 'summary' && ss === 'done')
        );
        const isExpanded = !!expandedSteps[i];
        const summary = !isExpanded ? stepCollapsedSummary(step.id, ss, batch, detailData, genLog, pct, qualitySummary) : null;

        return (
          <div key={step.id} className={`cd-pipe-step cd-pipe-step--${ss}`}>
            <div className="cd-pipe-track">
              <StepNode ss={ss} />
              {!isLast && <div className={`cd-pipe-line cd-pipe-line--${ss === 'done' ? 'done' : 'pending'}`} />}
            </div>

            <div className="cd-pipe-body">
              {/* Header row — clickable if step has content */}
              <div
                className={`cd-pipe-header${hasContent ? ' cd-pipe-header--clickable' : ''}`}
                onClick={() => hasContent && toggleStep(i)}
              >
                <span className="cd-pipe-label">{step.label}</span>
                <div className="cd-pipe-header-right">
                  {summary && <span className="cd-pipe-summary">{summary}</span>}
                  {!summary && statusLabel && <span className={`cd-pipe-status-text cd-pipe-status-text--${ss}`}>{statusLabel}</span>}
                  {hasContent && (
                    <span className="cd-pipe-chevron">{isExpanded ? <FiChevronUp size={12} /> : <FiChevronDown size={12} />}</span>
                  )}
                </div>
              </div>

              {/* Hint — only when expanded or no content */}
              {(isExpanded || !hasContent) && (
                <div className="cd-pipe-hint">{step.hint}</div>
              )}

              {/* Expanded content */}
              {isExpanded && (
                <>
                  {/* ── Step 1: Generate ── */}
                  {step.id === 'generate' && (
                    <div className="cd-pipe-content">
                      {genLog.length > 0 ? (
                        <div className="cd-detail-scroll">
                          {genLog.map((entry, j) => (
                            <div key={j} className="cd-log-entry">
                              <span className="cd-log-ts">{new Date(entry.ts).toLocaleTimeString()}</span>
                              <span className="cd-log-msg">{entry.msg}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="cd-pipe-subtext"><FiLoader className="cd-spin" size={10} /> Building course list…</div>
                      )}
                    </div>
                  )}

                  {/* ── Step 2: Queue ── */}
                  {step.id === 'queue' && (
                    <div className="cd-pipe-content">
                      {ss === 'active' && detailLoading && !detailData ? (
                        <div className="cd-pipe-subtext">Loading course list…</div>
                      ) : ss === 'active' && detailData?.type === 'courses' ? (
                        <>
                          <div className="cd-pipe-count-label">{detailData.list.length} courses queued</div>
                          <div className="cd-detail-scroll">
                            {detailData.list.map((name, j) => <div key={j} className="cd-course-item">{name}</div>)}
                          </div>
                        </>
                      ) : batch.total_count > 0 ? (
                        <div className="cd-pipe-count-label">
                          {batch.total_count} queued
                          {batch.skipped_count > 0 && <span className="cd-pipe-skipped"> · {batch.skipped_count} skipped (already in directory)</span>}
                        </div>
                      ) : null}
                    </div>
                  )}

                  {/* ── Step 3: Import ── */}
                  {step.id === 'import' && (
                    <div className="cd-pipe-content">
                      {batch.total_count > 0 && (
                        <div className="cd-pipe-progress">
                          <div className="cd-pipe-prog-bar">
                            <div className="cd-pipe-prog-fill" style={{
                              width: `${pct}%`,
                              background: ss === 'failed' ? '#ef4444' : ss === 'paused' ? '#f59e0b' : undefined,
                            }} />
                          </div>
                          <span className="cd-pipe-prog-label">{batch.processed_count} / {batch.total_count} · {pct}%</span>
                        </div>
                      )}
                      {(batch.success_count > 0 || batch.failed_count > 0) && (
                        <div className="cd-pipe-counts">
                          {batch.success_count > 0 && <span className="cd-pc-ok">{batch.success_count} done</span>}
                          {batch.failed_count > 0  && <span className="cd-pc-fail">{batch.failed_count} failed</span>}
                          {batch.skipped_count > 0 && <span className="cd-pc-skip">{batch.skipped_count} skipped</span>}
                          {(batch.total_count - batch.processed_count) > 0 && (
                            <span className="cd-pc-pending">{batch.total_count - batch.processed_count} pending</span>
                          )}
                        </div>
                      )}
                      {detailLoading && !detailData && <div className="cd-pipe-subtext">Loading…</div>}
                      {detailData?.type === 'ingestion' && (
                        <div className="cd-detail-scroll">
                          {detailData.jobs.map(job => <ImportJobRow key={job.id} job={job} />)}
                        </div>
                      )}
                    </div>
                  )}

                  {/* ── Step 4: Quality Summary ── */}
                  {step.id === 'summary' && ss === 'done' && (
                    <div className="cd-pipe-content">
                      <QualitySummary summary={qualitySummary} />
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Job card
// ---------------------------------------------------------------------------

const ACTIVE_STATUSES = ['generating', 'running', 'ready', 'paused'];

function defaultExpandedSteps(batchStatus) {
  const map = {
    generating: { 0: true },
    ready:      { 1: true },
    running:    { 2: true },
    paused:     { 2: true },
  };
  return map[batchStatus] || {};
}

function JobRow({ batch, onRefresh, onDelete }) {
  const { currentUser } = useAuth();
  const [actionLoading, setActionLoading] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [detailData, setDetailData]       = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [qualitySummary, setQualitySummary] = useState(null);

  // Job-level: active jobs open by default, terminal jobs collapsed
  const [pipelineOpen, setPipelineOpen] = useState(() => ACTIVE_STATUSES.includes(batch.status));

  // Step-level: open the currently active step by default
  const [expandedSteps, setExpandedSteps] = useState(() => defaultExpandedSteps(batch.status));

  // When status changes, open the newly-active step (but don't close others the user opened)
  useEffect(() => {
    setExpandedSteps(prev => ({ ...prev, ...defaultExpandedSteps(batch.status) }));
    if (ACTIVE_STATUSES.includes(batch.status)) setPipelineOpen(true);
  }, [batch.status]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleStep = (i) => setExpandedSteps(s => ({ ...s, [i]: !s[i] }));

  const authHeaders = useCallback(async () => {
    const token = currentUser ? await currentUser.getIdToken() : '';
    return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
  }, [currentUser]);

  // Clear detail cache whenever status changes
  useEffect(() => {
    setDetailData(null);
    setQualitySummary(null);
  }, [batch.status]);

  // Auto-load detail data for all active / terminal statuses
  useEffect(() => {
    if (!['ready', 'running', 'paused', 'complete', 'failed'].includes(batch.status)) return;
    let cancelled = false;
    const load = async () => {
      setDetailLoading(true);
      try {
        const token = currentUser ? await currentUser.getIdToken() : '';
        const headers = { Authorization: `Bearer ${token}` };
        if (batch.status === 'ready') {
          const res = await fetch(`${API}/batch-jobs/${batch.id}?include_courses=true`, { headers });
          if (res.ok && !cancelled) {
            const data = await res.json();
            setDetailData({ type: 'courses', list: data.course_list || [] });
          }
        } else {
          const res = await fetch(`${API}/batch-jobs/${batch.id}/ingestion-jobs`, { headers });
          if (res.ok && !cancelled) setDetailData({ type: 'ingestion', jobs: await res.json() });
        }
      } catch (_) {}
      if (!cancelled) setDetailLoading(false);
    };
    load();
    return () => { cancelled = true; };
  }, [batch.id, batch.status, batch.processed_count, currentUser]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load quality summary when complete
  useEffect(() => {
    if (batch.status !== 'complete') return;
    let cancelled = false;
    const load = async () => {
      try {
        const token = currentUser ? await currentUser.getIdToken() : '';
        const res = await fetch(`${API}/batch-jobs/${batch.id}/quality-summary`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok && !cancelled) setQualitySummary(await res.json());
      } catch (_) {}
    };
    load();
    return () => { cancelled = true; };
  }, [batch.id, batch.status, currentUser]);

  const doDelete = async () => {
    setConfirmDelete(false);
    setActionLoading('delete');
    try {
      const headers = await authHeaders();
      await fetch(`${API}/batch-jobs/${batch.id}`, { method: 'DELETE', headers });
      onDelete(batch.id);
    } catch (e) { notify.error(e.message); }
    finally { setActionLoading(null); }
  };

  const doAction = async (action) => {
    setActionLoading(action);
    try {
      const headers = await authHeaders();
      const res = await fetch(`${API}/batch-jobs/${batch.id}/${action}`, { method: 'POST', headers });
      if (!res.ok) { const err = await res.json().catch(() => ({})); notify.error(err.error || action); }
      onRefresh();
    } catch (e) { notify.error(e.message); }
    finally { setActionLoading(null); }
  };

  const isActive = ['generating', 'running'].includes(batch.status);
  const canDelete = !['generating', 'running'].includes(batch.status);
  const hasFailed = batch.failed_count > 0 && ['paused', 'complete', 'failed'].includes(batch.status);
  const createdAt = batch.created_at
    ? new Date(batch.created_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
    : '—';

  return (
    <div className={`cd-job cd-job--${batch.status}`}>
      {/* ── Header ── */}
      <div className="cd-job-main">

        {/* Left: name + meta */}
        <div className="cd-job-info">
          <div className="cd-job-name">{batch.name}</div>
          <div className="cd-job-meta">#{batch.id} · {batch.region ? `${batch.region} · ` : ''}{batch.country} · {createdAt}</div>
        </div>

        {/* Centre-left: target / actual count */}
        {batch.total_count > 0 && (
          <div className="cd-job-count">
            <span className="cd-job-count-num">{batch.total_count}</span>
            <span className="cd-job-count-lbl">{batch.status === 'generating' ? 'target' : 'courses'}</span>
          </div>
        )}

        {/* Centre-right: status badge + live dot */}
        <div className="cd-job-status-area">
          <StatusBadge status={batch.status} />
          {isActive && <span className="cd-live-dot" />}
        </div>

        {/* Action buttons */}
        <div className="cd-job-actions">
          {batch.status === 'ready' && (
            <button className="cd-icon-btn cd-icon-btn--primary" title="Start import" onClick={() => doAction('start')} disabled={actionLoading === 'start'}><FiPlay /></button>
          )}
          {batch.status === 'running' && (
            <button className="cd-icon-btn cd-icon-btn--warn" title="Pause" onClick={() => doAction('pause')} disabled={actionLoading === 'pause'}><FiPause /></button>
          )}
          {batch.status === 'paused' && (
            <button className="cd-icon-btn cd-icon-btn--primary" title="Resume" onClick={() => doAction('resume')} disabled={actionLoading === 'resume'}><FiPlay /></button>
          )}
          {batch.status === 'failed' && batch.total_count === 0 && (
            <button className="cd-icon-btn cd-icon-btn--primary" title="Retry list generation" onClick={() => doAction('retry-generation')} disabled={actionLoading === 'retry-generation'}><FiRefreshCw /></button>
          )}
          {hasFailed && (
            <button className="cd-icon-btn cd-icon-btn--warn" title={`Retry ${batch.failed_count} failed courses`} onClick={() => doAction('retry-failed')} disabled={actionLoading === 'retry-failed'}>
              <FiRotateCcw />
            </button>
          )}
          {['generating', 'ready', 'running', 'paused'].includes(batch.status) && (
            <button className="cd-icon-btn" title="Cancel" onClick={() => doAction('cancel')} disabled={actionLoading === 'cancel'}><FiX /></button>
          )}
          <button className="cd-icon-btn" title="Refresh" onClick={onRefresh}><FiRefreshCw /></button>
          {canDelete && (
            <button className="cd-icon-btn cd-icon-btn--danger" title="Delete" onClick={() => setConfirmDelete(true)} disabled={actionLoading === 'delete'}><FiTrash2 /></button>
          )}
        </div>

        {/* Far right: pipeline expand/collapse — fixed column, always visible */}
        <button
          className="cd-job-toggle"
          title={pipelineOpen ? 'Collapse' : 'Expand'}
          onClick={() => setPipelineOpen(o => !o)}
        >
          {pipelineOpen ? <FiChevronUp size={14} /> : <FiChevronDown size={14} />}
        </button>

      </div>

      {/* ── Vertical pipeline (collapsible) ── */}
      {pipelineOpen && (
        <WorkflowPipeline
          batch={batch}
          detailData={detailData}
          detailLoading={detailLoading}
          qualitySummary={qualitySummary}
          expandedSteps={expandedSteps}
          toggleStep={toggleStep}
        />
      )}

      <ConfirmationModal
        tone="danger"
        isOpen={confirmDelete}
        title="Delete job?"
        message={`Delete "${batch.name}"? This cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={doDelete}
        onCancel={() => setConfirmDelete(false)}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Create form
// ---------------------------------------------------------------------------

function CreateBatchForm({ onCreated, onCancel, prefill }) {
  const { currentUser } = useAuth();
  const [form, setForm] = useState(prefill || DEFAULT_FORM);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const selectedCountry = COUNTRIES.find(c => c.value === form.country) || COUNTRIES[0];
  const regionGroups = regionOptions(form.country);
  const description = buildDescription(form.tier, form.types);

  const toggleType = (type) => setForm(f => ({
    ...f,
    types: f.types.includes(type) ? f.types.filter(t => t !== type) : [...f.types, type],
  }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const token = currentUser ? await currentUser.getIdToken() : '';
      const tierLabel = TIERS.find(t => t.value === form.tier)?.label || form.tier;
      const name = form.region ? `${form.region} – ${tierLabel}` : `${form.country} – ${tierLabel}`;
      const res = await fetch(`${API}/batch-jobs/generate-list`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name,
          description,
          country: form.country,
          region: form.region || null,
          target_count: Number(form.targetCount),
          delay_seconds: Number(form.delaySeconds),
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setError(err.error || 'Failed to start job');
        return;
      }
      onCreated(await res.json());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="cd-form-page">
      <div className="cd-form-header">
        <h2 className="cd-form-title">New Download Job</h2>
        <p className="cd-form-hint">
          The AI will build a course list matching your criteria, then import each course with full WHS slope and rating data.
        </p>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="cd-form-row">
          <div className="cd-field">
            <label className="cd-label">Country</label>
            <select className="cd-select" value={form.country} onChange={e => setForm(f => ({ ...f, country: e.target.value, region: '' }))}>
              {COUNTRIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </div>
          {regionGroups.length > 0 && (
            <div className="cd-field">
              <label className="cd-label">{selectedCountry.regionLabel} <span className="cd-optional">(optional)</span></label>
              <select className="cd-select" value={form.region} onChange={e => setForm(f => ({ ...f, region: e.target.value }))}>
                <option value="">All of {selectedCountry.label}</option>
                {regionGroups.map(g => (g.group
                  ? <optgroup key={g.group} label={g.group}>
                      {g.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </optgroup>
                  : g.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)
                ))}
              </select>
            </div>
          )}
        </div>

        <div className="cd-section-label">Quality tier</div>
        <div className="cd-tier-grid">
          {TIERS.map(t => (
            <button key={t.value} type="button"
              className={`cd-tier-btn ${form.tier === t.value ? 'cd-tier-btn--active' : ''}`}
              onClick={() => setForm(f => ({ ...f, tier: t.value }))}>
              <span className="cd-tier-name">{t.label}</span>
              <span className="cd-tier-hint">{t.hint}</span>
            </button>
          ))}
        </div>

        <div className="cd-section-label">Course type <span className="cd-optional">(optional)</span></div>
        <div className="cd-chips">
          {TYPES.map(t => (
            <button key={t.value} type="button" className={`cd-chip ${form.types.includes(t.value) ? 'cd-chip--active' : ''}`} onClick={() => toggleType(t.value)}>
              {form.types.includes(t.value) && <FiCheck size={11} />} {t.label}
            </button>
          ))}
        </div>

        <div className="cd-description-preview">
          <span className="cd-description-label">AI will search for:</span>
          <span className="cd-description-text">"{description}" in {form.region ? `${form.region}, ` : ''}{form.country}</span>
        </div>

        <div className="cd-form-row">
          <div className="cd-field">
            <label className="cd-label">Target count <span className="cd-optional">(1–500)</span></label>
            <input className="cd-input" type="number" min="1" max="500"
              value={form.targetCount}
              onChange={e => setForm(f => ({ ...f, targetCount: e.target.value }))}
              onBlur={e => {
                const n = Math.min(500, Math.max(1, Number(e.target.value) || 1));
                setForm(f => ({ ...f, targetCount: n }));
              }} />
          </div>
          <div className="cd-field">
            <label className="cd-label">Delay between courses</label>
            <div className="cd-input-suffix-wrap">
              <input className="cd-input" type="number" min="2" max="60"
                value={form.delaySeconds}
                onChange={e => setForm(f => ({ ...f, delaySeconds: e.target.value }))} />
              <span className="cd-input-suffix">sec</span>
            </div>
          </div>
        </div>

        {error && <div className="cd-error">{error}</div>}

        <div className="cd-form-actions">
          {onCancel && <button type="button" className="cd-btn cd-btn--ghost" onClick={onCancel}>Cancel</button>}
          <button type="submit" className="cd-btn cd-btn--primary" disabled={loading}>
            {loading ? 'Starting…' : <><FiDownload /> Generate Course List</>}
          </button>
        </div>
      </form>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

const BOTTOM_NAV = [
  { name: 'Leaderboard', route: '/leaderboard', icon: <FiBarChart2 size={24} />, needsSociety: true },
  { name: 'Rounds',      route: '/tournaments/scorecard-entry', icon: <FiGrid size={24} />, needsSociety: true },
  { name: 'Play Golf',   route: '/play', icon: <GiGolfTee size={24} />, needsSociety: true },
  { name: 'Manage',      route: '/setup', icon: <FiSettings size={24} />, active: true },
];

const PAGE_SIZE = 10;

/* ── Prompt Lab card — entry point to the dedicated /course-downloader/prompts
   page; shows a badge when prompt-improvement proposals are pending review. */
function PromptLabCard() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [pending, setPending] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = await currentUser.getIdToken();
        const res = await fetch(`${API}/admin/prompts`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return; // non-sys-admin or error: leave the badge hidden
        const data = await res.json();
        // Only suggestions never used and written for the prompt in use —
        // the ones there is anything to decide about.
        const count = Object.values(data.tasks || {})
          .flat().filter(v => v.state === 'new').length;
        if (!cancelled) setPending(count);
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [currentUser]);

  return (
    <button className="cd-promptlab-card" onClick={() => navigate('/course-downloader/prompts')}>
      <FiCpu size={18} className="cd-promptlab-card-icon" />
      <span className="cd-promptlab-card-text">
        <span className="cd-promptlab-card-title">Prompt Lab</span>
        <span className="cd-promptlab-card-sub">analyse failures · improve prompts</span>
      </span>
      {pending > 0 && <span className="cd-promptlab-card-badge">{pending} new</span>}
      <FiChevronRight size={18} className="cd-promptlab-card-chev" />
    </button>
  );
}

export default function CourseDownloader() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [batches, setBatches] = useState([]);
  const [creating, setCreating] = useState(false);
  const [prefill, setPrefill] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const pollRef = useRef(null);

  const fetchBatches = useCallback(async () => {
    if (!currentUser) return;
    try {
      const token = await currentUser.getIdToken();
      const res = await fetch(`${API}/batch-jobs`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setBatches(await res.json());
    } catch (_) {}
    setLoading(false);
  }, [currentUser]);

  useEffect(() => {
    fetchBatches();
    pollRef.current = setInterval(fetchBatches, 6000);
    return () => clearInterval(pollRef.current);
  }, [fetchBatches]);

  const handleCreated = (batch) => {
    setCreating(false);
    setPrefill(null);
    setBatches(prev => [batch, ...prev]);
  };

  const handleDelete = (id) => setBatches(prev => prev.filter(b => b.id !== id));

  const handleNewJob = (sourceBatch) => {
    if (sourceBatch) {
      setPrefill({
        country: COUNTRIES.find(c => c.value === sourceBatch.country) ? sourceBatch.country : 'United Kingdom',
        region: isKnownRegion(sourceBatch.country, sourceBatch.region) ? sourceBatch.region : '',
        tier: 'top',
        types: [],
        targetCount: Math.min(500, sourceBatch.total_count || 100),
        delaySeconds: sourceBatch.delay_seconds || 5,
      });
    } else {
      setPrefill(null);
    }
    setCreating(true);
  };

  const hasActive = batches.some(b => ['generating', 'running'].includes(b.status));

  if (creating) {
    return (
      <div className="cd-page">
        <PageHeader title="Course Downloader" backTo="/course-downloader" backLabel="Back" />
        <div className="cd-content">
          <CreateBatchForm
            onCreated={handleCreated}
            onCancel={() => { setCreating(false); setPrefill(null); }}
            prefill={prefill}
          />
        </div>
        <BottomNav items={BOTTOM_NAV} />
      </div>
    );
  }

  return (
    <div className="cd-page">
      <PageHeader title="Course Downloader" backTo="/setup" backLabel="Back to Manage" />

      <div className="cd-content">
        <PromptLabCard />

        {hasActive && (
          <div className="cd-live-bar">
            <span className="cd-live-dot" /> Auto-refreshing every 6s
          </div>
        )}

        {loading && <div className="cd-loading">Loading…</div>}

        {!loading && batches.length === 0 && (
          <div className="cd-empty">
            <FiDownload size={36} />
            <p>No download jobs yet.</p>
          </div>
        )}

        {batches.length > 0 && (() => {
          const totalPages = Math.ceil(batches.length / PAGE_SIZE);
          const paginated = batches.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);
          return (
            <>
              <div className="cd-job-list">
                {paginated.map(b => (
                  <JobRow
                    key={b.id}
                    batch={b}
                    onRefresh={fetchBatches}
                    onDelete={handleDelete}
                  />
                ))}
              </div>

              {totalPages > 1 && (
                <div className="cd-pagination">
                  <button className="cd-page-btn" disabled={page === 0} onClick={() => setPage(p => p - 1)}>‹ Prev</button>
                  <span className="cd-page-info">Page {page + 1} of {totalPages}</span>
                  <button className="cd-page-btn" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>Next ›</button>
                </div>
              )}
            </>
          );
        })()}

        <div className="cd-cta-row">
          <button className="cd-cta-btn cd-cta-btn--primary" onClick={() => handleNewJob(null)}>
            <FiPlus /> New Job
          </button>
          <button className="cd-cta-btn cd-cta-btn--ghost" onClick={() => navigate('/setup')}>
            Cancel
          </button>
        </div>
      </div>

      <BottomNav items={BOTTOM_NAV} />
    </div>
  );
}
